// pages/AssetsFormPage/AssetsFormPage.jsx
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Auxiliares
import PdfCanvasViewer from './PdfCanvasViewer';
import OverlayEditor from './OverlayEditor';
import ChartPresetPicker from './ChartPresetPicker';

// Contextos
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// API
import { useAlgoritmoTesteService } from '../../services/algoritmoTesteServices';
import { useReportAssetService } from '../../services/reportAssetsService';

// Estilos
import styles from './assetsformpage.module.css';

// Constantes
import { URLS } from '../../constants/urls';

function AssetsFormPage() {
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { idTeste } = useParams();
  const { pathname, search } = useLocation();

  const isEditing = pathname.includes('atualizar');

  const { listarAlgoritmosPorTeste } = useAlgoritmoTesteService();
  const { criarAsset, atualizarAsset, listarTodosDoAlgoritmo } = useReportAssetService();

  // ===== Estado principal =====
  const [publicadoEscolhido, setPublicadoEscolhido] = useState(null); // algoritmo published
  const [perfis, setPerfis] = useState([]); // [{id, name}]
  const [tituloAlg, setTituloAlg] = useState('');

  const [hasProfileGroups, setHasProfileGroups] = useState(false);   // se o algoritmo tem agrupamentos
  const [profileGroupNames, setProfileGroupNames] = useState([]);   // labels dos grupos

  // Arquivo comum
  const [fileComum, setFileComum] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null); // objectURL OU URL do servidor
  const pdfUrlRef = useRef(null);
  const [compact, setCompact] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  // Arquivos por perfil: { [profileId]: { file, preview } }
  const [filesPerfis, setFilesPerfis] = useState({});

  // Editor overlay
  const [chartPreset, setChartPreset] = useState('bar'); // preset default para novos gráficos
  const [items, setItems] = useState([]); // overlay items
  const [selectedOverlayId, setSelectedOverlayId] = useState(null);

  // Página corrente do viewer
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(1);

  // Assets existentes (para UPDATE vs CREATE)
  const existingRef = useRef({ common: null, byProfile: {} });

  // =============================
  // Helpers para "acompanhamento"
  // =============================
  const isTrackPreset = (preset) => String(preset || '').toLowerCase().endsWith('_track');

  /**
   * Normaliza um item de chart:
   * - garante preset/dataMode
   * - se preset for *_track, salva mode/tracking (compatível com isAcompanhamentoChart() do backend)
   * - se não for track, remove esses campos
   */
  const normalizeChartItem = useCallback((it, opts = {}) => {
    if (!it || it.type !== 'chart') return it;

    const {
      defaultPreset = hasProfileGroups ? 'radar' : chartPreset,
      defaultDataMode = hasProfileGroups ? 'groups' : 'profiles',
    } = opts;

    const preset = it.preset || defaultPreset;
    const dataMode = it.dataMode || defaultDataMode;

    const track = isTrackPreset(preset);

    if (track) {
      return {
        ...it,
        preset,
        dataMode,
        mode: it.mode || 'acompanhamento',
        tracking: it.tracking ?? true,
      };
    }

    // não-track => remove flags relacionadas
    const { mode, tracking, acompanhamento, ...rest } = it;
    return { ...rest, preset, dataMode };
  }, [chartPreset, hasProfileGroups]);

  /**
   * Normaliza todos os charts do array (para salvar / carregar / editar).
   */
  const normalizeAllCharts = useCallback((arr = []) => {
    const defaultPreset = hasProfileGroups ? 'radar' : chartPreset;
    const defaultDataMode = hasProfileGroups ? 'groups' : 'profiles';

    return (arr || []).map(it => {
      if (it?.type !== 'chart') return it;
      return normalizeChartItem(it, { defaultPreset, defaultDataMode });
    });
  }, [chartPreset, hasProfileGroups, normalizeChartItem]);

  // Util: parse JSON se vier string
  const safeParse = (v, fallback = null) => {
    if (!v) return fallback;
    if (typeof v === 'object') return v;
    try { return JSON.parse(v); } catch { return fallback; }
  };

  // Helper: monta URL ABSOLUTA do arquivo em assets/reports/
  const buildAssetUrl = (storageUri) => {
    if (!storageUri) return null;
    const clean = String(storageUri).replace(/^\/+/, '');
    const needsReports = !/^reports\//.test(clean);
    const path = `assets/${needsReports ? 'reports/' : ''}${clean}`;
    const url = `${URLS.IMG_PATH}${path}`; // sua IMG_PATH termina com "/assets/"
    return url.replace(/assets\/assets\//, 'assets/');
  };

  // Helper: adiciona cache-bust (uma única vez)
  const withNoCacheOnce = (url) => {
    try {
      const u = new URL(url, window.location.href);
      if (!u.searchParams.has('_v')) u.searchParams.set('_v', Date.now().toString());
      return u.toString();
    } catch {
      return `${url}${url.includes('?') ? '&' : '?'}_v=${Date.now()}`;
    }
  };

  // Query param ?alg=...
  const algoritmoIdFromQuery = useMemo(() => {
    const sp = new URLSearchParams(search);
    return sp.get('alg') || null;
  }, [search]);

  // Labels de perfis e de agrupamentos para preview
  const profileLabels = useMemo(
    () => perfis.map(p => p.name),
    [perfis]
  );
  const groupLabels = useMemo(
    () => profileGroupNames,
    [profileGroupNames]
  );

  // Chart selecionado (para tipo e modo)
  const selectedChart = useMemo(
    () => (items || []).find(it => it.id === selectedOverlayId && it.type === 'chart'),
    [items, selectedOverlayId]
  );

  const pickerPresetValue = selectedChart?.preset || chartPreset;
  const selectedChartDataMode = selectedChart?.dataMode || (hasProfileGroups ? 'groups' : 'profiles');

  // ======== Helpers p/ “sugerir” lista/tabela na criação (não usados em edição) ========
  const createPredominantListItem = (page = 1) => ({
    id: crypto.randomUUID(),
    type: 'list',
    mode: 'predominantProfiles',
    page,
    x: 0.10, y: 0.48, w: 0.75, h: 0.20
  });

  const ensurePredominantListExistsOnce = (arr = [], page = 1) => {
    const has = (arr || []).some(it => it.type === 'list' && it.mode === 'predominantProfiles');
    return has ? arr : [...arr, createPredominantListItem(page)];
  };

  const createGroupsTableItem = (page = 1) => ({
    id: crypto.randomUUID(),
    type: 'table',
    mode: 'profileGroupsSummary',
    page,
    x: 0.10, y: 0.72, w: 0.80, h: 0.18
  });

  const ensureGroupsTableExistsOnce = (arr = [], page = 1) => {
    const has = (arr || []).some(it => it.type === 'table' && it.mode === 'profileGroupsSummary');
    return has ? arr : [...arr, createGroupsTableItem(page)];
  };

  // ======== Sanitização ========
  const pickKeys = (obj, keys) => {
    const out = {};
    keys.forEach(k => { if (obj[k] !== undefined) out[k] = obj[k]; });
    return out;
  };

  // Mantém os charts completos (incluindo mode/tracking). Apenas "list predominantProfiles" é reduzida.
  const sanitizeOverlayItems = (arr = []) =>
    (arr || []).map(it => {
      if (it.type === 'list' && it.mode === 'predominantProfiles') {
        return pickKeys(it, ['id', 'type', 'mode', 'page', 'x', 'y', 'w', 'h']);
      }
      return it;
    });

  // ===== Helper de label para cada tipo de item (para sidebar) =====
  const getItemLabel = (it) => {
    if (!it) return 'Elemento';

    if (it.type === 'chart') {
      const rawPreset = it.preset || 'bar';
      const isTrack = rawPreset.endsWith('_track');
      const p = isTrack ? rawPreset.replace(/_track$/, '') : rawPreset;

      const mode = it.dataMode || (hasProfileGroups ? 'groups' : 'profiles');
      const modeLabel = hasProfileGroups
        ? mode === 'profiles' ? 'por Perfil' : 'por Agrupamento'
        : '';

      let tipo = 'Gráfico';
      if (p === 'bar') tipo = 'Gráfico de Barras';
      else if (p === 'column') tipo = 'Gráfico de Colunas';
      else if (p === 'pie') tipo = 'Gráfico de Pizza';
      else if (p === 'donut') tipo = 'Gráfico Donut';
      else if (p === 'radar') tipo = 'Gráfico Radar';

      const trackLabel = isTrack ? ' • com acompanhamento' : '';
      const base = modeLabel ? `${tipo} (${modeLabel})` : tipo;
      return `${base}${trackLabel}`;
    }

    if (it.type === 'highlight') {
      return 'Gráfico maior/menor perfil';
    }

    if (it.type === 'text') {
      const key = it.content?.key;
      if (it.content?.mode === 'token') {
        if (key === 'participant.name') return 'Texto: Nome do participante';
        if (key === 'report.date') return 'Texto: Data do relatório';
        return `Texto: Token (${key})`;
      }
      return 'Texto livre';
    }

    if (it.type === 'list' && it.mode === 'predominantProfiles') {
      return 'Lista de perfis predominantes';
    }

    if (it.type === 'table' && it.mode === 'profileGroupsSummary') {
      return 'Tabela de agrupamentos de perfil';
    }

    return 'Elemento';
  };

  // ===== Carregar algoritmo publicado e perfis =====
  useEffect(() => {
    const carregar = async () => {
      console.groupCollapsed('[AssetsFormPage] carregar algoritmo publicado e perfis');
      showLoader('Carregando algoritmo publicado e perfis');
      console.time('listarAlgoritmosPorTeste');
      try {
        const res = await listarAlgoritmosPorTeste({ testeId: idTeste, status: 'published', limit: 10 });
        console.timeEnd('listarAlgoritmosPorTeste');

        if (!res?.success) {
          showToast(res?.message || 'Falha ao buscar algoritmos', 3500, 'error');
          return;
        }

        const lista = Array.isArray(res.data) ? res.data : [];
        if (lista.length === 0) {
          showToast('Este teste não possui algoritmo publicado.', 3500, 'warning');
          return;
        }

        const pick = algoritmoIdFromQuery
          ? lista.find(a => (a.idEncoded ?? a.id) === algoritmoIdFromQuery)
          : lista[0];

        const alg = pick || lista[0];
        setPublicadoEscolhido(alg);
        setTituloAlg(alg?.titulo || 'Algoritmo publicado');

        // Perfis + agrupamentos
        let perfisExtraidos = [];
        let hasGroups = false;
        let groupNames = [];
        try {
          const jsonStr = alg.algoritmo_json || alg.algoritmoJson || alg.json || null;
          const obj = safeParse(jsonStr, null);

          if (obj?.profilesV2 && Array.isArray(obj.profilesV2)) {
            perfisExtraidos = obj.profilesV2.map(p => ({ id: p.id, name: p.name }));
          } else if (obj?.profiles && Array.isArray(obj.profiles)) {
            perfisExtraidos = obj.profiles.map(n => ({ id: String(n), name: String(n) }));
          }

          if (obj?.profileGroups && Array.isArray(obj.profileGroups) && obj.profileGroups.length > 0) {
            hasGroups = true;
            groupNames = obj.profileGroups.map(pg => pg.label || pg.id || 'Grupo');
          }
        } catch {
          // ignore parse errors
        }

        setPerfis(perfisExtraidos);
        setHasProfileGroups(hasGroups);
        setProfileGroupNames(groupNames);

        // PDFs por perfil: estrutura base
        const base = {};
        perfisExtraidos.forEach(p => { base[p.id] = { file: null, preview: null }; });
        setFilesPerfis(base);

        // Ajuste do preset default dependendo do algoritmo
        if (hasGroups) {
          // se o preset atual não faz sentido, forçamos radar como default para novos
          setChartPreset(prev => (prev ? prev : 'radar'));
        }

        // Edição: carregar assets existentes
        const algIdEncoded = alg.idEncoded || alg.id;
        if (isEditing && algIdEncoded) {
          await carregarAssetsExistentes(algIdEncoded, perfisExtraidos, hasGroups);
        } else {
          setShowEditor(false);
          setCompact(false);
          if (hasGroups) setChartPreset('radar');
        }
      } catch (err) {
        console.error('Erro carregar algoritmo/perfis:', err);
        showToast('Erro ao carregar dados do algoritmo publicado', 3500, 'error');
      } finally {
        hideLoader();
        console.groupEnd();
      }
    };

    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTeste, isEditing, algoritmoIdFromQuery]);

  // ===== Carrega assets (comum + perfis) do algoritmo e configura estado =====
  const carregarAssetsExistentes = async (algoritmoIdEncoded, perfisExtraidos, hasGroups) => {
    console.groupCollapsed('[AssetsFormPage] carregarAssetsExistentes');
    showLoader('Carregando devolutivas existentes');
    console.time('listarTodosDoAlgoritmo');
    try {
      const resp = await listarTodosDoAlgoritmo(algoritmoIdEncoded, { onlyActive: true });
      console.timeEnd('listarTodosDoAlgoritmo');

      if (!resp?.success) {
        showToast(resp?.message || 'Falha ao listar assets', 3500, 'warning');
        return;
      }

      const rows = Array.isArray(resp.data) ? resp.data : [];

      const common = rows.find(r => Number(r.is_common) === 1) || null;
      const byProfile = {};
      rows.filter(r => Number(r.is_common) === 0).forEach(r => {
        if (r.profile_id) byProfile[r.profile_id] = r;
      });

      existingRef.current = { common, byProfile };

      // PDF COMUM
      if (common?.storage_uri) {
        const absCommon = buildAssetUrl(common.storage_uri);
        const stabilized = withNoCacheOnce(absCommon);
        if (pdfUrlRef.current !== stabilized) {
          pdfUrlRef.current = stabilized;
          setPdfUrl(stabilized);
        }
      } else {
        pdfUrlRef.current = null;
        setPdfUrl(null);
      }

      // Overlay existente — SEM adicionar caixas padrão
      const existingOverlay = safeParse(common?.overlay_json, common?.overlay_json) || null;
      const overlayItemsRaw = (existingOverlay?.items && Array.isArray(existingOverlay.items))
        ? existingOverlay.items
        : [];

      // Sanitiza + normaliza charts (preset/dataMode + acompanhamento por preset)
      let sanitized = sanitizeOverlayItems(overlayItemsRaw);
      sanitized = normalizeAllCharts(sanitized);

      const firstChart = sanitized.find(i => i.type === 'chart');
      if (firstChart?.preset) setChartPreset(firstChart.preset);
      setSelectedOverlayId(firstChart?.id || (sanitized[0]?.id ?? null));

      setItems(sanitized);

      // Em edição: começa fechado
      setShowEditor(false);
      setCompact(true);

      // PDFs por perfil
      setFilesPerfis(prev => {
        const next = { ...prev };
        perfisExtraidos.forEach(p => {
          const r = byProfile[p.id];
          if (r?.storage_uri) {
            next[p.id] = { file: null, preview: buildAssetUrl(r.storage_uri) };
          }
        });
        return next;
      });
    } catch (e) {
      console.error('Erro ao carregar devolutivas existentes:', e);
      showToast('Erro ao carregar devolutivas existentes', 3500, 'error');
    } finally {
      hideLoader();
      console.groupEnd();
    }
  };

  // ===== Uploads =====
  const onFileComumChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') {
      showToast('Apenas PDF é permitido.', 2500, 'info');
      return;
    }
    setFileComum(f);
    const url = URL.createObjectURL(f);
    setPdfUrl(prev => (prev === url ? prev : url));
    pdfUrlRef.current = url;
    setCurrentPage(1);
    setCompact(false);
    setShowEditor(true);

    const editingCommonAlreadyExists = isEditing && !!existingRef.current.common;

    // Se estamos EDITANDO um asset que já existe, NÃO cria caixas padrão de novo
    if (!editingCommonAlreadyExists) {
      setItems(prev => {
        const hasChart = prev.some(it => it.type === 'chart');
        let next = prev;

        if (!hasChart) {
          const newId = crypto.randomUUID();

          const baseChart = normalizeChartItem({
            id: newId,
            type: 'chart',
            preset: hasProfileGroups ? 'radar' : chartPreset,
            dataMode: hasProfileGroups ? 'groups' : 'profiles',
            page: 1,
            x: 0.10, y: 0.12, w: 0.55, h: 0.28,
            legend: false,
            title: ''
          });

          next = [...next, baseChart];
          setSelectedOverlayId(newId);
        }

        // Sugerir lista/tabela APENAS na criação
        next = ensurePredominantListExistsOnce(next, 1);
        if (hasProfileGroups) {
          next = ensureGroupsTableExistsOnce(next, 1);
        }

        // Sanitiza + normaliza charts
        next = sanitizeOverlayItems(next);
        next = normalizeAllCharts(next);

        return next;
      });

      if (hasProfileGroups) {
        setChartPreset('radar');
      }
    }
  };

  const onFilePerfilChange = (profileId, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') {
      showToast('Apenas PDF é permitido.', 2500, 'info');
      return;
    }
    const url = URL.createObjectURL(f);
    setFilesPerfis(prev => ({
      ...prev,
      [profileId]: { file: f, preview: url },
    }));
  };

  // ===== Toolbar lateral: adicionar/remover elementos =====
  const addChartItem = () => {
    if (!pdfUrl) {
      showToast('Selecione o PDF comum antes de adicionar elementos.', 3000, 'info');
      return;
    }
    const newId = crypto.randomUUID();

    const base = normalizeChartItem({
      id: newId,
      type: 'chart',
      preset: hasProfileGroups ? 'radar' : chartPreset,
      dataMode: hasProfileGroups ? 'groups' : 'profiles',
      page: currentPage,
      x: 0.10, y: 0.12, w: 0.55, h: 0.28,
      legend: false,
      title: ''
    });

    setItems(prev => normalizeAllCharts([...prev, base]));
    setSelectedOverlayId(newId);
  };

  // NOVO: gráfico de maior/menor perfil
  const addHighlightItem = () => {
    if (!pdfUrl) {
      showToast('Selecione o PDF comum antes de adicionar elementos.', 3000, 'info');
      return;
    }
    const id = crypto.randomUUID();
    const item = {
      id,
      type: 'highlight',
      mode: 'maxMinProfiles',
      page: currentPage,
      x: 0.08, y: 0.10,
      w: 0.40, h: 0.24
    };
    setItems(prev => [...prev, item]);
    setSelectedOverlayId(id);
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(it => it.id !== id));
    if (selectedOverlayId === id) {
      setSelectedOverlayId(null);
    }
  };

  const sidebarItems = useMemo(
    () => items || [],
    [items]
  );

  // ===== Toolbar: preset por gráfico + modo de dados =====
  const handlePresetChange = (preset) => {
    // Preset default para novos gráficos
    setChartPreset(preset);

    // Se não tiver chart selecionado, não altera os existentes
    if (!selectedOverlayId) return;

    setItems(prev =>
      normalizeAllCharts(
        prev.map(it =>
          it.id === selectedOverlayId && it.type === 'chart'
            ? { ...it, preset }
            : it
        )
      )
    );
  };

  const handleChartDataModeChange = (mode) => {
    if (!selectedOverlayId) return;
    setItems(prev =>
      normalizeAllCharts(
        prev.map(it =>
          it.id === selectedOverlayId && it.type === 'chart'
            ? { ...it, dataMode: mode }
            : it
        )
      )
    );
  };

  const addTextToken = (tokenKey) => {
    if (!pdfUrl) {
      showToast('Selecione o PDF comum antes de adicionar elementos.', 3000, 'info');
      return;
    }
    const isDate = tokenKey === 'report.date';
    const id = crypto.randomUUID();
    const item = {
      id,
      type: 'text',
      page: currentPage,
      x: 0.12, y: isDate ? 0.20 : 0.16,
      w: isDate ? 0.22 : 0.34, h: 0.05,
      content: { mode: 'token', key: tokenKey, ...(isDate ? { format: 'dd/MM/yyyy' } : {}) },
      style: { font: 'Inter', size: 12, weight: 600, align: 'left' },
      fit: 'shrink'
    };
    setItems(prev => [...prev, item]);
    setSelectedOverlayId(id);
  };

  // ===== Navegação do viewer =====
  const onViewerReady = ({ numPages: np }) => setNumPages(np);
  const onPageChange = (p) => setCurrentPage(p);

  // ===== Recebe alterações do OverlayEditor =====
  const rafRef = useRef(0);
  const bufferRef = useRef(items);
  useEffect(() => { bufferRef.current = items; }, [items]);

  const handleItemsChange = useCallback((nextItems) => {
    const numericSanitized = (nextItems || []).map(it => ({
      ...it,
      x: Number(it.x ?? 0),
      y: Number(it.y ?? 0),
      w: Number(it.w ?? 0),
      h: Number(it.h ?? 0),
      page: Number(it.page ?? 1),
    }));

    // Sanitiza (listas) + normaliza charts (acompanhamento por preset)
    const fullySanitized = normalizeAllCharts(sanitizeOverlayItems(numericSanitized));

    const prev = bufferRef.current;
    const sameLen = prev.length === fullySanitized.length;
    const shallowSame = sameLen && prev.every((p, i) => p === fullySanitized[i]);
    if (shallowSame) return;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      bufferRef.current = fullySanitized;
      setItems(fullySanitized);
    });
  }, [normalizeAllCharts]);

  // ===== Helpers de payload =====
  const buildOverlayJson = () => {
    const assetId = existingRef.current?.common?.id ?? null;

    // Antes de salvar, garante normalização final
    const normalizedItems = normalizeAllCharts(sanitizeOverlayItems(items));

    const payload = {
      items: normalizedItems,
      meta: {
        assetId: assetId ?? '(definir após upload do PDF comum)',
        version: 1,
        hasProfileGroups: !!hasProfileGroups
      }
    };
    console.debug('[AssetsFormPage] buildOverlayJson (sanitized+normalized):', payload);
    return payload;
  };

  // ===== Salvar posições (apenas overlay) =====
  const handleSaveLayoutOnly = () => {
    const algoritmoIdEncoded = publicadoEscolhido?.idEncoded || publicadoEscolhido?.id;
    if (!algoritmoIdEncoded) {
      showToast('Algoritmo publicado sem ID válido.', 3000, 'error');
      return;
    }
    setCompact(true);
    setShowEditor(false);
    showToast('Posições salvas (local) e preview fechado.', 2500, 'success');
  };

  // ===== Submit (CREATE/UPDATE) =====
  const handleSubmit = async (e) => {
    e.preventDefault();

    const algoritmoIdEncoded = publicadoEscolhido?.idEncoded || publicadoEscolhido?.id;
    if (!algoritmoIdEncoded) {
      showToast('Algoritmo publicado sem ID válido.', 3500, 'error');
      return;
    }

    console.groupCollapsed('[AssetsFormPage] handleSubmit');
    console.time('submitTotal');
    try {
      showLoader(isEditing ? 'Atualizando devolutivas' : 'Criando devolutivas');

      // 1) Common
      const commonExisting = existingRef.current.common;
      const overlayJson = buildOverlayJson();

      if (isEditing && commonExisting) {
        const fd = new FormData();
        fd.append('id', String(commonExisting.id));
        fd.append('algoritmoId', algoritmoIdEncoded);
        fd.append('profileId', ''); // comum
        fd.append('overlayJson', JSON.stringify(overlayJson));
        if (fileComum) fd.append('file', fileComum, fileComum.name);

        const upRes = await atualizarAsset(fd);
        if (!upRes?.success) throw new Error(upRes?.message || 'Falha ao atualizar PDF comum');

      } else {
        if (!fileComum) {
          showToast('Selecione o PDF comum para criar.', 3500, 'warning');
          hideLoader();
          return;
        }
        const fd = new FormData();
        fd.append('algoritmoId', algoritmoIdEncoded);
        fd.append('overlayJson', JSON.stringify(overlayJson));
        fd.append('file', fileComum, fileComum.name);

        const crRes = await criarAsset(fd);
        if (!crRes?.success) throw new Error(crRes?.message || 'Falha ao criar PDF comum');

        existingRef.current.common = { id: crRes.id, storage_uri: crRes.filename, is_common: 1 };
      }

      // 2) Profiles — criar/atualizar se houver novo arquivo
      const byProfile = existingRef.current.byProfile || {};
      for (const p of perfis) {
        const entry = filesPerfis[p.id];
        if (!entry?.file) continue;

        const existing = byProfile[p.id];
        if (existing) {
          const fd = new FormData();
          fd.append('id', String(existing.id));
          fd.append('algoritmoId', algoritmoIdEncoded);
          fd.append('profileId', p.id);
          fd.append('file', entry.file, entry.file.name);

          const up = await atualizarAsset(fd);
          if (!up?.success) throw new Error(up?.message || `Falha ao atualizar PDF do perfil ${p.name}`);

        } else {
          const fd = new FormData();
          fd.append('algoritmoId', algoritmoIdEncoded);
          fd.append('profileId', p.id);
          fd.append('file', entry.file, entry.file.name);

          const cr = await criarAsset(fd);
          if (!cr?.success) throw new Error(cr?.message || `Falha ao criar PDF do perfil ${p.name}`);

          byProfile[p.id] = { id: cr.id, storage_uri: cr.filename, is_common: 0, profile_id: p.id };
        }
      }

      showToast(isEditing ? 'Devolutivas atualizadas!' : 'Devolutivas criadas!', 2800, 'success');
      navigate(`/devolutivas`);
    } catch (err) {
      console.error('handleSubmit error:', err);
      showToast(err?.message || 'Falha ao salvar devolutivas', 3500, 'error');
    } finally {
      console.timeEnd('submitTotal');
      hideLoader();
      console.groupEnd();
    }
  };

  const breadcrumbLabel = useMemo(() => {
    if (!publicadoEscolhido) return undefined;
    return publicadoEscolhido.titulo || 'algoritmo';
  }, [publicadoEscolhido]);

  return (
    <div className={styles.AssetsFormPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbLabel ? [{ id: idTeste, label: breadcrumbLabel }] : []} />
        <h1 className={styles.title}>
          {isEditing ? 'Editar Devolutivas' : 'Cadastro de Devolutivas'}
        </h1>
        <FriendlyPhrase page={isEditing ? 'atualizarDevolutivas' : 'adicionarDevolutivas'} />
      </div>

      <form onSubmit={handleSubmit} className={styles.formulario} encType="multipart/form-data">
        <div className={styles.generalArea}>
          <h2 className={styles.sectionTitle}>
            Algoritmo publicado {tituloAlg ? `• ${tituloAlg}` : ''}
          </h2>

          {/* PDF Comum + Editor */}
          <label>
            PDF Comum:
            <div className={styles.fileInputWrapper}>
              <span className={styles.fileLabel}>Selecionar PDF</span>
              <input
                type="file"
                accept="application/pdf"
                className={styles.fileInput}
                onChange={onFileComumChange}
              />
            </div>
          </label>

          {/* Editor (condicional) */}
          {pdfUrl && showEditor && (
            <div
              className={styles.editorViewport}
              style={compact ? { maxHeight: 260, overflow: 'hidden', borderColor: '#e5e5e5' } : undefined}
            >
              <div className={styles.editorWrapper}>
                {/* Toolbar lateral */}
                <aside className={styles.editorSidebar}>
                  <p className={styles.editorSidebarTitle}>Elementos do PDF</p>

                  <button
                    type="button"
                    className={styles.sidebarButton}
                    onClick={addChartItem}
                  >
                    + Gráfico
                  </button>

                  <button
                    type="button"
                    className={styles.sidebarButton}
                    onClick={addHighlightItem}
                  >
                    + Gráfico maior/menor
                  </button>

                  <div className={styles.sidebarItemList}>
                    {sidebarItems.length === 0 ? (
                      <small className={styles.sidebarEmptyHint}>
                        Nenhum elemento ainda.
                      </small>
                    ) : (
                      sidebarItems.map((it) => (
                        <div
                          key={it.id}
                          className={`${styles.sidebarItemRow} ${
                            selectedOverlayId === it.id ? styles.sidebarItemRowSelected : ''
                          }`}
                          onClick={() => setSelectedOverlayId(it.id)}
                        >
                          <span className={styles.sidebarItemLabel}>
                            {getItemLabel(it)} (pág. {it.page || 1})
                          </span>
                          <button
                            type="button"
                            className={styles.sidebarItemRemove}
                            onClick={(e) => { e.stopPropagation(); removeItem(it.id); }}
                          >
                            ×
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </aside>

                {/* Área principal */}
                <div className={styles.editorMain}>
                  <div className={styles.editorToolbar}>
                    <div className={styles.toolbarGroup}>
                      <ChartPresetPicker
                        value={pickerPresetValue}
                        onChange={handlePresetChange}
                      />

                      {hasProfileGroups && selectedChart && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12 }}>
                          <span style={{ fontSize: '0.8rem', color: '#555' }}>Modo de dados:</span>
                          <button
                            type="button"
                            className={styles.tokenBtn}
                            style={selectedChartDataMode === 'groups'
                              ? { background: '#002D4C', color: '#fff' }
                              : {}}
                            onClick={() => handleChartDataModeChange('groups')}
                          >
                            Agrupamentos
                          </button>
                          <button
                            type="button"
                            className={styles.tokenBtn}
                            style={selectedChartDataMode === 'profiles'
                              ? { background: '#002D4C', color: '#fff' }
                              : {}}
                            onClick={() => handleChartDataModeChange('profiles')}
                          >
                            Perfis
                          </button>
                        </div>
                      )}
                    </div>

                    <div className={styles.toolbarGroup}>
                      <button type="button" className={styles.tokenBtn} onClick={() => addTextToken('participant.name')}>
                        Adicionar Nome
                      </button>
                      <button type="button" className={styles.tokenBtn} onClick={() => addTextToken('report.date')}>
                        Adicionar Data
                      </button>
                    </div>

                    <div className={styles.toolbarGroupRight}>
                      <div className={styles.pageControls}>
                        <button
                          type="button"
                          className={styles.pageBtn}
                          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                          disabled={currentPage <= 1}
                        >
                          ◀
                        </button>
                        <span className={styles.pageLabel}>
                          Página {currentPage} / {numPages}
                        </span>
                        <button
                          type="button"
                          className={styles.pageBtn}
                          onClick={() => onPageChange(Math.min(numPages, currentPage + 1))}
                          disabled={currentPage >= numPages}
                        >
                          ▶
                        </button>
                      </div>

                      <button type="button" className={styles.saveBtn} onClick={handleSaveLayoutOnly}>
                        Salvar posições
                      </button>
                    </div>
                  </div>

                  <div
                    className={styles.pdfStage}
                    style={
                      compact
                        ? {
                            transform: 'scale(0.7)',
                            transformOrigin: 'top left',
                            height: 0,
                            paddingBottom: 0,
                            marginBottom: -30,
                            pointerEvents: 'none'
                          }
                        : undefined
                    }
                  >
                    <PdfCanvasViewer key={pdfUrl} fileUrl={pdfUrl} page={currentPage} onReady={onViewerReady} />
                    <OverlayEditor
                      page={currentPage}
                      items={items}
                      profileLabels={profileLabels}
                      groupLabels={groupLabels}
                      onChange={handleItemsChange}
                      disabled={compact}
                      selectedId={selectedOverlayId}
                      onSelectItem={setSelectedOverlayId}
                    />
                  </div>

                  <small className={styles.mutedNote}>
                    Arraste e redimensione as áreas. As cores e valores do gráfico são definidos no backend.
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Botão para reabrir o editor quando estiver fechado */}
          {pdfUrl && !showEditor && (
            <div style={{ marginTop: '.5rem' }}>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => { setShowEditor(true); setCompact(false); }}
              >
                Reabrir editor
              </button>
            </div>
          )}
        </div>

        {/* PDFs por Perfil */}
        <div className={styles.generalArea}>
          <h2 className={styles.sectionTitle}>PDFs por Perfil</h2>

          {perfis.length === 0 && (
            <p style={{ margin: '0.5rem 0' }}>
              Nenhum perfil encontrado no algoritmo publicado.
            </p>
          )}

          <div className={styles.perfisGrid}>
            {perfis.map((p) => {
              const entry = filesPerfis[p.id] || {};
              return (
                <div key={p.id} className={styles.perfilCard}>
                  <div className={styles.perfilHeader}>
                    <strong>{p.name}</strong>
                    <small className={styles.perfilId}>{p.id}</small>
                  </div>

                  <div className={styles.fileInputWrapper}>
                    <span className={styles.fileLabel}>Selecionar PDF</span>
                    <input
                      type="file"
                      accept="application/pdf"
                      className={styles.fileInput}
                      onChange={(e) => onFilePerfilChange(p.id, e)}
                    />
                  </div>

                  {entry.preview && (
                    <embed
                      src={entry.preview}
                      type="application/pdf"
                      className={styles.pdfPreviewSmall}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.submitArea}>
          <button type="submit" className={styles.submitButton}>
            {isEditing ? 'Atualizar Devolutivas' : 'Salvar Devolutivas'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AssetsFormPage;
